/**
 * Form Builder Utilities
 *
 * Helper functions for the form builder including:
 * - Field name generation
 * - Form schema conversion
 * - Validation helpers
 * - Conditional logic evaluation
 */

import type {
  FormFieldDefinition,
  FormDefinition,
  FormSettings,
  FieldType,
  ValidationRule,
  FieldCondition,
  ConditionalOperator,
  SelectOption,
} from './types';
import { FIELD_TYPE_CONFIGS } from './constants';
import type { FormFieldConfig, ValidationRule as FormFieldValidationRule } from '@/components/ui/forms/FormField';

// ============================================================================
// ID Generation
// ============================================================================

/**
 * Generate a unique field ID
 */
export function generateFieldId(): string {
  return `field_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Generate a unique form ID
 */
export function generateFormId(): string {
  return `form_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Generate a field name from a label
 */
export function generateFieldName(label: string, existingNames: string[]): string {
  // Convert label to snake_case
  const baseName = label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  // Ensure unique name
  let name = baseName || 'field';
  let counter = 1;

  while (existingNames.includes(name)) {
    name = `${baseName}_${counter}`;
    counter++;
  }

  return name;
}

// ============================================================================
// Schema Conversion
// ============================================================================

/**
 * Convert FormFieldDefinition to FormFieldConfig for DynamicForm
 */
export function toFormFieldConfig(field: FormFieldDefinition): FormFieldConfig {
  const config: FormFieldConfig = {
    name: field.name,
    type: mapFieldType(field.type),
    label: field.label,
    labelKey: field.labelKey,
    placeholder: field.placeholder,
    placeholderKey: field.placeholderKey,
    description: field.description,
    descriptionKey: field.descriptionKey,
    helpText: field.helpText,
    helpTextKey: field.helpTextKey,
    required: field.required,
    disabled: field.disabled,
    defaultValue: field.defaultValue,
    options: field.options,
    min: field.min,
    max: field.max,
    step: field.step,
    rows: field.rows,
    accept: field.accept,
    multiple: field.multiple,
    span: field.colSpan,
  };

  // Convert validation rules
  if (field.validation && field.validation.length > 0) {
    config.rules = field.validation.map((rule) => convertValidationRule(rule));
  }

  // Add conditional visibility
  if (field.showWhen && field.showWhen.length > 0) {
    config.visible = (values: Record<string, unknown>) =>
      evaluateConditions(field.showWhen!, values, field.showWhenLogic);
  }

  return config;
}

/**
 * Map form builder field type to FormField type
 */
function mapFieldType(type: FieldType): FormFieldConfig['type'] {
  // Layout elements should be filtered out before calling this
  const typeMap: Partial<Record<FieldType, FormFieldConfig['type']>> = {
    text: 'text',
    email: 'email',
    password: 'password',
    number: 'number',
    textarea: 'textarea',
    select: 'select',
    multiselect: 'multiselect',
    checkbox: 'checkbox',
    switch: 'switch',
    radio: 'radio',
    date: 'date',
    datetime: 'datetime',
    file: 'file',
    slider: 'slider',
    rating: 'rating',
    richtext: 'richtext',
    time: 'datetime', // Map to datetime for now
    image: 'file', // Map to file
    color: 'text', // Map to text (could be enhanced)
  };

  return typeMap[type] ?? 'text';
}

/**
 * Convert FormBuilder ValidationRule to FormField ValidationRule
 */
function convertValidationRule(rule: ValidationRule): FormFieldValidationRule {
  switch (rule.type) {
    case 'email':
      return { type: 'email', messageKey: rule.messageKey };
    case 'url':
      return { type: 'url', messageKey: rule.messageKey };
    case 'minLength':
      return {
        type: 'minLength',
        value: rule.value as number,
        messageKey: rule.messageKey,
      };
    case 'maxLength':
      return {
        type: 'maxLength',
        value: rule.value as number,
        messageKey: rule.messageKey,
      };
    case 'pattern':
      return {
        type: 'custom',
        validate: (value: unknown) => {
          if (!value) return null;
          const regex = new RegExp(rule.value as string);
          return regex.test(String(value)) ? null : rule.message;
        },
      };
    case 'min':
    case 'max':
      return {
        type: 'custom',
        validate: (value: unknown) => {
          const num = Number(value);
          if (isNaN(num)) return null;
          if (rule.type === 'min' && num < (rule.value as number)) {
            return rule.message;
          }
          if (rule.type === 'max' && num > (rule.value as number)) {
            return rule.message;
          }
          return null;
        },
      };
    default:
      return {
        type: 'custom',
        validate: () => null,
      };
  }
}

/**
 * Convert FormDefinition fields to FormFieldConfig array for DynamicForm
 */
export function toFormFieldConfigs(form: FormDefinition): FormFieldConfig[] {
  // Filter out layout elements and convert to FormFieldConfig
  return form.fields
    .filter((field) => !FIELD_TYPE_CONFIGS[field.type].isLayoutElement)
    .map(toFormFieldConfig);
}

/**
 * Get initial values from form definition
 */
export function getInitialValues(form: FormDefinition): Record<string, unknown> {
  const values: Record<string, unknown> = {};

  for (const field of form.fields) {
    if (!FIELD_TYPE_CONFIGS[field.type].isLayoutElement) {
      values[field.name] = field.defaultValue ?? getDefaultValueForType(field.type);
    }
  }

  return values;
}

/**
 * Get default value for a field type
 */
export function getDefaultValueForType(type: FieldType): unknown {
  return FIELD_TYPE_CONFIGS[type].defaultValue;
}

// ============================================================================
// Conditional Logic
// ============================================================================

/**
 * Evaluate a single condition
 */
export function evaluateCondition(
  condition: FieldCondition,
  values: Record<string, unknown>
): boolean {
  const fieldValue = values[condition.field];
  const compareValue = condition.value;

  switch (condition.operator) {
    case 'eq':
      return fieldValue === compareValue;
    case 'neq':
      return fieldValue !== compareValue;
    case 'gt':
      return Number(fieldValue) > Number(compareValue);
    case 'lt':
      return Number(fieldValue) < Number(compareValue);
    case 'gte':
      return Number(fieldValue) >= Number(compareValue);
    case 'lte':
      return Number(fieldValue) <= Number(compareValue);
    case 'contains':
      return String(fieldValue).includes(String(compareValue));
    case 'notContains':
      return !String(fieldValue).includes(String(compareValue));
    case 'isEmpty':
      return (
        fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === '' ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      );
    case 'isNotEmpty':
      return (
        fieldValue !== undefined &&
        fieldValue !== null &&
        fieldValue !== '' &&
        (!Array.isArray(fieldValue) || fieldValue.length > 0)
      );
    default:
      return true;
  }
}

/**
 * Evaluate multiple conditions with AND/OR logic
 */
export function evaluateConditions(
  conditions: FieldCondition[],
  values: Record<string, unknown>,
  logic: 'and' | 'or' = 'and'
): boolean {
  if (conditions.length === 0) return true;

  if (logic === 'and') {
    return conditions.every((c) => evaluateCondition(c, values));
  } else {
    return conditions.some((c) => evaluateCondition(c, values));
  }
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Check if a field name is valid
 */
export function isValidFieldName(name: string): boolean {
  // Must start with letter or underscore, contain only alphanumeric and underscore
  return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
}

/**
 * Check for duplicate field names in a form
 */
export function findDuplicateNames(fields: FormFieldDefinition[]): string[] {
  const seen = new Set<string>();
  const duplicates: string[] = [];

  for (const field of fields) {
    if (seen.has(field.name)) {
      duplicates.push(field.name);
    } else {
      seen.add(field.name);
    }
  }

  return duplicates;
}

/**
 * Validate a form definition
 */
export function validateFormDefinition(form: FormDefinition): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!form.name.trim()) {
    errors.push('Form name is required');
  }

  if (form.fields.length === 0) {
    errors.push('Form must have at least one field');
  }

  const duplicates = findDuplicateNames(form.fields);
  if (duplicates.length > 0) {
    errors.push(`Duplicate field names: ${duplicates.join(', ')}`);
  }

  for (const field of form.fields) {
    if (!field.name.trim()) {
      errors.push(`Field "${field.label || field.id}" has no name`);
    } else if (!isValidFieldName(field.name)) {
      errors.push(
        `Field name "${field.name}" is invalid. Use only letters, numbers, and underscores.`
      );
    }

    // Validate options for selection fields
    if (FIELD_TYPE_CONFIGS[field.type].hasOptions && (!field.options || field.options.length === 0)) {
      errors.push(`Field "${field.label}" requires at least one option`);
    }
  }

  return { isValid: errors.length === 0, errors };
}

// ============================================================================
// Field Helpers
// ============================================================================

/**
 * Check if a field type is a layout element
 */
export function isLayoutElement(type: FieldType): boolean {
  return FIELD_TYPE_CONFIGS[type].isLayoutElement;
}

/**
 * Check if a field type supports options
 */
export function hasOptions(type: FieldType): boolean {
  return FIELD_TYPE_CONFIGS[type].hasOptions;
}

/**
 * Check if a field type supports min/max
 */
export function hasMinMax(type: FieldType): boolean {
  return FIELD_TYPE_CONFIGS[type].hasMinMax;
}

/**
 * Get field icon
 */
export function getFieldIcon(type: FieldType): string {
  return FIELD_TYPE_CONFIGS[type].icon;
}

/**
 * Get field label
 */
export function getFieldTypeLabel(type: FieldType): string {
  return FIELD_TYPE_CONFIGS[type].label;
}

// ============================================================================
// Options Helpers
// ============================================================================

/**
 * Parse options from a string (one option per line: value|label or just value)
 */
export function parseOptionsString(input: string): SelectOption[] {
  return input
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [value, label] = line.split('|').map((s) => s.trim());
      return { value, label: label || value };
    });
}

/**
 * Format options to a string for editing
 */
export function formatOptionsString(options: SelectOption[]): string {
  return options
    .map((opt) => (opt.label !== opt.value ? `${opt.value}|${opt.label}` : opt.value))
    .join('\n');
}

// ============================================================================
// JSON Import/Export
// ============================================================================

/**
 * Safely parse JSON with error handling
 */
export function safeParseJSON<T>(json: string): { success: true; data: T } | { success: false; error: string } {
  try {
    const data = JSON.parse(json) as T;
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
  }
}

/**
 * Download JSON as a file
 */
export function downloadJSON(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ============================================================================
// Drag & Drop Helpers
// ============================================================================

/**
 * Calculate drop index based on mouse position
 */
export function calculateDropIndex(
  mouseY: number,
  containerRect: DOMRect,
  itemRects: DOMRect[],
  itemCount: number
): number {
  // Get relative Y position
  const relativeY = mouseY - containerRect.top;

  // Find the position where the item should be dropped
  for (let i = 0; i < itemRects.length; i++) {
    const itemRect = itemRects[i];
    const itemMiddle = itemRect.top - containerRect.top + itemRect.height / 2;

    if (relativeY < itemMiddle) {
      return i;
    }
  }

  return itemCount;
}

// ============================================================================
// Clipboard Helpers
// ============================================================================

/**
 * Copy text to clipboard with fallback
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

/**
 * Read text from clipboard
 */
export async function readFromClipboard(): Promise<string | null> {
  try {
    return await navigator.clipboard.readText();
  } catch {
    return null;
  }
}
